import {Button, Loader, Modal} from '@logistics-frontend/blocks'
import {useI18n} from '@logistics-frontend/core/i18n'
import {add, get, noop, pipe, toNumber} from '@logistics-frontend/utils'
import React, {useCallback, useState} from 'react'

import {AddCardButton} from '../add-card-button'
import {CardListSkeleton} from '../card-list-skeleton'
import {PaymentCardList} from '../payment-card-list'
import {classes} from './CardListFormModal.classes'
import {MAX_AVAILABLE_CARDS_AMOUNT} from './CardListFormModal.consts'
import {CardListFormModalProps} from './CardListFormModal.types'

const CardListFormModal: React.FC<CardListFormModalProps> = ({
  title,
  description,
  selectedCard,
  cards = [],
  onAddNewCard,
  onBound,
  onClose = noop,
  isCardsPending = false,
  isCardBounding = false,
  isMobile = false,
}) => {
  const t = useI18n()
  const [card, setCard] = useState(selectedCard)

  const submitHandler = useCallback(() => {
    if (card === undefined) {
      return
    }

    onBound(card)
  }, [card, onBound])

  const buttonSize = isMobile ? 'm' : 'l'
  const cardsInSkeletonAmount = pipe(cards, get('length'), toNumber, add(2))
  const isDisabledPaymentAddButton = cards.length >= MAX_AVAILABLE_CARDS_AMOUNT

  return (
    <Modal className={classes.modal} onClose={onClose} opened>
      <Modal.Header
        text={description || t('card_list_modal.bound_message')}
        title={title || t('card_list_modal.select_payment_method')}
      />
      <Modal.Body p="m">
        {isCardsPending ? (
          isMobile ? (
            <Loader fullscreenHeight fullWidth />
          ) : (
            <CardListSkeleton cardsCount={cardsInSkeletonAmount} />
          )
        ) : (
          <PaymentCardList cards={cards} onSelectCard={setCard} selectedCard={card}>
            <AddCardButton disabled={isDisabledPaymentAddButton} onPress={onAddNewCard} />
          </PaymentCardList>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Controls>
          {isMobile && (
            <Button onPress={onClose} size={buttonSize} fullWidth>
              {t('card_list_form.back')}
            </Button>
          )}
          <Button
            disabled={!card}
            loading={isCardBounding || isCardsPending}
            onPress={submitHandler}
            size={buttonSize}
            variant="action"
          >
            {t('phoenix.add_new_card.submit')}
          </Button>
        </Modal.Footer.Controls>
      </Modal.Footer>
    </Modal>
  )
}

CardListFormModal.displayName = 'CardListFormModal'

export default React.memo(CardListFormModal)
